import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import {
  Error as ErrorIcon,
  AccessTime,
  Phone,
  Email,
} from "@mui/icons-material";
import QuoteLinkContext, { useQuoteLink } from "../contexts/QuoteLinkContext";
import { QuoteHeader } from "../components/quoteLink/QuoteHeader";
import { QuoteDetails } from "../components/quoteLink/QuoteDetails";
import { ActionButtons } from "../components/quoteLink/ActionButtons";
import { StatusIndicator } from "../components/quoteLink/StatusIndicator";
import { TermsAndConditions } from "../components/quoteLink/TermsAndConditions";
import { ErrorBoundary } from "../components/quoteLink/ErrorBoundary";
import { useQuoteLinkService } from "../hooks/useQuoteLinkService";
import { usePhoneValidation } from "../hooks/usePhoneValidation";
import { usePixelEventTracker } from "../hooks/usePixelEventTracker";
import {
  logInfo,
  logError,
  logSuccess,
  logDebug,
} from "../shared/utils/logger.js";

/**
 * QuoteLinkPage - Main page for viewing and interacting with quote links
 * Mobile-first responsive design for quote viewing and actions
 */
const QuoteLinkPage = () => {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { quote, loading, error, actions } = useContext(QuoteLinkContext);
  const { loadQuote } = useQuoteLink(tokenId);
  const { acceptQuote, rejectQuote, validateOwnership } = useQuoteLinkService();
  const {
    validatePhone,
    phoneError,
    isValidPhone,
    formatPhone,
    clearPhoneError,
  } = usePhoneValidation();
  const {
    trackQuoteView,
    trackQuoteAccept,
    trackQuoteShare,
    trackQuoteReject,
    isInitialized: isPixelInitialized,
  } = usePixelEventTracker();

  const CONTACT_PHONE = import.meta.env.VITE_CONTACT_PHONE || "+27 67 674 4632";
  const CONTACT_EMAIL =
    import.meta.env.VITE_CONTACT_EMAIL || "support@bubakii.co.za";

  const [phone, setPhone] = useState(searchParams.get("phone") || "");
  const [phoneMismatch, setPhoneMismatch] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(null); // null=unknown, true/false
  const [ownershipChecking, setOwnershipChecking] = useState(false);

  useEffect(() => {
    const phoneParam = searchParams.get("phone");
    logDebug("QuoteLinkPage: phone parameter from URL", {
      phoneParam,
      currentPhone: phone,
    });
  }, [searchParams, phone]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  // Debounced ownership validation on phone change
  useEffect(() => {
    let timer;
    const run = async () => {
      try {
        if (!phone || !isValidPhone(phone)) {
          setPhoneMismatch(false);
          setPhoneVerified(null);
          return;
        }
        setOwnershipChecking(true);
        const res = await validateOwnership(tokenId, formatPhone(phone));
        const mismatch =
          res?.verified === false && res?.code === "PHONE_MISMATCH";
        setPhoneMismatch(mismatch);
        setPhoneVerified(
          !mismatch && res?.verified === true ? true : mismatch ? false : null
        );
      } catch (e) {
        const mismatch = e?.code === "PHONE_MISMATCH";
        setPhoneMismatch(mismatch);
        setPhoneVerified(mismatch ? false : null);
      } finally {
        setOwnershipChecking(false);
      }
    };
    timer = setTimeout(run, 400);
    return () => clearTimeout(timer);
  }, [phone, tokenId, isValidPhone, validateOwnership, formatPhone]);

  useEffect(() => {
    if (quote?.status === "accepted" || quote?.status === "pending_payment") {
      navigate(`/quote/${tokenId}/payment`, { replace: true });
    }
  }, [quote?.status, tokenId, navigate]);

  useEffect(() => {
    if (
      error === "phone_required" ||
      (error && error.includes("Phone number is required"))
    ) {
      setShowPhoneDialog(true);
    }
  }, [error]);

  useEffect(() => {
    logDebug("QuoteLinkPage: phone state changed", { phone });
  }, [phone]);

  // Facebook Pixel tracking effects
  useEffect(() => {
    if (quote && isPixelInitialized()) {
      try {
        trackQuoteView(quote.id, quote.estimatedCost);
        logInfo("Facebook Pixel: Tracked quote view", {
          quoteId: quote.id,
          estimatedCost: quote.estimatedCost,
        });
      } catch (error) {
        logError("Facebook Pixel: Failed to track quote view", error);
      }
    }
  }, [quote, trackQuoteView, isPixelInitialized]);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    setPhoneVerified(null);
    if (value && !isValidPhone(value)) {
      validatePhone(value);
    } else {
      clearPhoneError();
    }
  };

  const handlePhoneBlur = () => {
    if (isValidPhone(phone)) {
      setPhone(formatPhone(phone));
      clearPhoneError();
    }
  };

  const handlePhoneSubmit = async () => {
    if (!validatePhone(phone)) {
      return;
    }

    const currentPhone = formatPhone(phone);
    setPhone(currentPhone);
    setShowPhoneDialog(false);
    await loadQuote(tokenId, currentPhone);
  };

  const handleAcceptQuote = async () => {
    if (!termsAccepted) {
      setAlert({
        show: true,
        message: "Please accept the terms and conditions to proceed",
        severity: "warning",
      });
      return;
    }

    if (
      !phone ||
      !isValidPhone(phone) ||
      phoneMismatch ||
      phoneVerified !== true
    ) {
      setAlert({
        show: true,
        message: phoneMismatch
          ? "This phone doesn’t match the number that received this link."
          : "Please verify your phone number first.",
        severity: "error",
      });
      setShowPhoneDialog(true);
      return;
    }

    setActionLoading(true);
    try {
      const result = await acceptQuote(tokenId, formatPhone(phone), {
        termsAccepted: true,
      });
      if (result.success) {
        // Track Facebook Pixel Purchase event
        if (isPixelInitialized()) {
          try {
            trackQuoteAccept(quote.id, quote.estimatedCost);
            logSuccess("Facebook Pixel: Tracked quote acceptance", {
              quoteId: quote.id,
              estimatedCost: quote.estimatedCost,
            });
          } catch (error) {
            logError("Facebook Pixel: Failed to track quote acceptance", error);
          }
        }

        setAlert({ show: false, message: "", severity: "info" });
        setShowPhoneDialog(false);
        await loadQuote(tokenId, formatPhone(phone));
        // Reload the page to show updated quote status
        window.location.reload();
      }
    } catch (err) {
      setAlert({
        show: true,
        message: "Failed to accept quote. Please try again.",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectQuote = async () => {
    setActionLoading(true);
    try {
      const result = await rejectQuote(tokenId, formatPhone(phone), {
        reason: rejectReason || "No reason provided",
      });
      if (result.success) {
        // Track Facebook Pixel QuoteRejected event
        if (isPixelInitialized()) {
          try {
            trackQuoteReject(quote.id, rejectReason || "No reason provided");
            logInfo("Facebook Pixel: Tracked quote rejection", {
              quoteId: quote.id,
              reason: rejectReason || "No reason provided",
            });
          } catch (error) {
            logError("Facebook Pixel: Failed to track quote rejection", error);
          }
        }

        setAlert({
          show: true,
          message: "Quote rejected. Thank you for your feedback.",
          severity: "info",
        });
        setShowRejectDialog(false);
        setRejectReason("");
      }
    } catch (err) {
      setAlert({
        show: true,
        message: "Failed to reject quote. Please try again.",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleShareQuote = () => {
    // Track Facebook Pixel QuoteShared event
    if (isPixelInitialized()) {
      try {
        const shareMethod = navigator.share ? "native_share" : "clipboard";
        trackQuoteShare(quote.id, shareMethod);
        logInfo("Facebook Pixel: Tracked quote sharing", {
          quoteId: quote.id,
          shareMethod,
        });
      } catch (error) {
        logError("Facebook Pixel: Failed to track quote sharing", error);
      }
    }

    if (navigator.share) {
      navigator.share({
        title: "Your Quote",
        text: `View your quote: ${quote?.id}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setAlert({
        show: true,
        message: "Quote link copied to clipboard!",
        severity: "success",
      });
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading your quote...
        </Typography>
      </Box>
    );
  }

  if (error === "rate_limited") {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Too Many Requests
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You’ve made too many requests in a short period. Please wait a few
              minutes and try again.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              If you need urgent assistance, contact our team using the options
              below.
            </Typography>
            <Stack spacing={1} direction="column" sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Phone />}
                onClick={() => window.open(`tel:${CONTACT_PHONE.replace(/[\s\-()]/g, "")}`)}
              >
                Call {CONTACT_PHONE}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Email />}
                onClick={() => window.open(`mailto:${CONTACT_EMAIL}`)}
              >
                Email {CONTACT_EMAIL}
              </Button>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error && !showPhoneDialog) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Quote Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!quote) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading your quote...
        </Typography>
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
        {phoneMismatch && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            This phone doesn’t match the number that received this link. Please
            use the phone that received the WhatsApp/SMS.
          </Alert>
        )}

        {alert.show && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{
              position: "fixed",
              top: 16,
              left: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            {alert.message}
          </Alert>
        )}

        <Container maxWidth="sm" sx={{ py: 2 }}>
          <QuoteHeader quote={quote} onShare={handleShareQuote} />
          <QuoteDetails quote={quote} />
          <StatusIndicator status={quote?.status} />
          <TermsAndConditions
            accepted={termsAccepted}
            onAcceptChange={setTermsAccepted}
            disabled={quote?.status !== "pending" && quote?.status !== "sent"}
          />
          <ActionButtons
            quote={quote}
            tokenId={tokenId}
            onAccept={handleAcceptQuote}
            onReject={() => setShowRejectDialog(true)}
            onRequestCall={() => {}}
            loading={actionLoading || ownershipChecking}
            termsAccepted={termsAccepted}
            disabled={phoneMismatch || phoneVerified !== true}
          />

          {actions && actions.length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                <Stack spacing={1}>
                  {actions.slice(0, 3).map((action, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1}>
                      <AccessTime fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {action.actionSummary} - {""}
                        {new Date(action.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Container>

        <Dialog
          open={showPhoneDialog}
          onClose={() => {}}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Verify Your Phone Number</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please enter your phone number to access this quote.
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              placeholder="+27123456789"
              error={!!phoneError || phoneMismatch}
              helperText={
                phoneMismatch
                  ? "This phone doesn’t match the number that received this link."
                  : phoneError
              }
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handlePhoneSubmit}
              variant="contained"
              fullWidth
              disabled={
                !isValidPhone(phone) ||
                phoneMismatch ||
                ownershipChecking ||
                phoneVerified !== true
              }
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showRejectDialog}
          onClose={() => setShowRejectDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Quote</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please let us know why you&apos;re rejecting this quote. Your
              feedback helps us improve.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason (Optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Price too high, Different requirements, etc."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              onClick={handleRejectQuote}
              variant="contained"
              color="error"
            >
              Reject Quote
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ErrorBoundary>
  );
};

export default QuoteLinkPage;
